import { FC, useMemo, useCallback } from "react";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { FilterChip } from "@/components/filters/FilterChip";
import { LightTooltip } from "@/components/LightTooltip";
import { useFilterDraft, useBuyersJourneyLabel } from "@/hooks/filters";
import { BuyersJourneyCheckboxList } from "@/lib/company-filter/modules/intent/components/filters";
import { IntentTooltipContent } from "@/lib/company-filter/modules/intent/components";
import { BUYERS_JOURNEY_TOOLTIP_SECTIONS } from "@/lib/company-filter/modules/intent/constants";
import { useIntentFiltersStorage } from "../hooks/useIntentFiltersStorage";

export const BuyersJourneyFilter: FC = () => {
  const { intentFilters, setIntentFilters } = useIntentFiltersStorage();

  const journeyDraft = useFilterDraft(intentFilters.buyersJourneyIds, (value) =>
    setIntentFilters((prev) => ({ ...prev, buyersJourneyIds: value }))
  );

  const handleClear = useCallback(() => {
    setIntentFilters((prev) => ({ ...prev, buyersJourneyIds: [] }));
  }, [setIntentFilters]);

  const label = useBuyersJourneyLabel(intentFilters.buyersJourneyIds);
  const count = intentFilters.buyersJourneyIds.length;

  const getSelectedCount = useCallback(() => count, [count]);

  const popoverOptions = useMemo(
    () => ({
      onClearButtonClick: journeyDraft.clearDraft,
      onCancelButtonClick: journeyDraft.cancelChanges,
      onApplyButtonClick: journeyDraft.applyChanges,
      disableApplyButton: !journeyDraft.hasChanges,
      disableCancelButton: false,
    }),
    [journeyDraft]
  );

  const helpIcon = useMemo(
    () => (
      <LightTooltip
        placement="bottom"
        title={
          <IntentTooltipContent sections={BUYERS_JOURNEY_TOOLTIP_SECTIONS} />
        }
      >
        <IconButton
          size="small"
          aria-label="Buyer's journey help"
          disableRipple
          sx={{ p: 0.25 }}
        >
          <HelpOutlineIcon fontSize="inherit" sx={{ fontSize: 18 }} />
        </IconButton>
      </LightTooltip>
    ),
    []
  );

  return (
    <FilterChip
      label="Buyer's Journey"
      getSelectedCount={getSelectedCount}
      onDelete={count > 0 ? handleClear : undefined}
      renderLabel={() => (
        <Stack flexDirection="row" gap="3px">
          <Typography color="secondary">Buyer's Journey:</Typography>
          <Typography>{label}</Typography>
        </Stack>
      )}
      popoverOptions={popoverOptions}
      titleAdornment={helpIcon}
    >
      <Box id="intent-buyers-journey-filter" mb={1}>
        <BuyersJourneyCheckboxList
          selectedIds={journeyDraft.draft}
          onChange={journeyDraft.updateDraft}
        />
      </Box>
    </FilterChip>
  );
};
